import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Stack, LinearProgress, Button, Chip } from '@mui/material';
import { ShoppingCart, WarningAmber, ChevronRight, LocalShipping } from '@mui/icons-material';
import { financeWorkspaceService } from '../../services/financeWorkspaceService';
import { formatIndianNumber } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { RouterLink } from '../../utils/linkNav';

const MaterialRequirementsControl: React.FC = () => {
  const navigate = useNavigate();
  const [d, setD] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { financeWorkspaceService.procurementControl().then(setD).catch(() => setD(null)).finally(() => setLoading(false)); }, []);
  const m = d?.metrics || {};
  const C = useControlRoomColors();

  const Metric: React.FC<{ label: string; value: React.ReactNode; color?: string; sub?: string }> = ({ label, value, color, sub }) => (
    <Box sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 2, p: 2 }}>
      <Typography sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontWeight: 800, fontSize: 22, color: color || C.text }}>{value}</Typography>
      {sub && <Typography sx={{ color: C.sub, fontSize: 11 }}>{sub}</Typography>}
    </Box>
  );

  return (
    <Box sx={{ bgcolor: C.bg, minHeight: '100%', m: -3, color: C.text }}>
      <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${C.line}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: `linear-gradient(90deg, ${C.panel}, ${C.bg})` }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <ShoppingCart sx={{ color: C.accent }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 20 }}>Procurement Control</Typography>
            <Typography sx={{ color: C.sub, fontSize: 13 }}>Do I have the materials required?</Typography>
          </Box>
        </Stack>
        <Button variant="outlined" onClick={() => navigate('/procurement/orders')} sx={{ color: C.text, borderColor: C.line }}>Purchase Orders</Button>
      </Box>
      {loading && <LinearProgress />}

      <Box sx={{ px: 3, py: 2.5, display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', md: 'repeat(5,1fr)' }, gap: 1.5 }}>
        <Metric label="Open Commitments" value={m.openCommitment != null ? formatIndianNumber(m.openCommitment) : '—'} sub={`${m.openPOs || 0} POs`} />
        <Metric label="Material Shortages" value={m.shortages ?? '—'} color={m.shortages > 0 ? C.bad : C.text} />
        <Metric label="Outstanding Bills" value={m.outstandingValue != null ? formatIndianNumber(m.outstandingValue) : '—'} color={C.warn} sub={`${m.outstandingBills || 0} bills`} />
        <Metric label="Spend (FY)" value={m.spendFY != null ? formatIndianNumber(m.spendFY) : '—'} />
        <Metric label="Open POs" value={m.openPOs ?? '—'} color={C.accent} />
      </Box>

      <Box sx={{ px: 3, pb: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' }, gap: 2 }}>
        {/* Material requirements */}
        <Box sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 2, p: 2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1.5 }}>Material Requirements</Typography>
          <Stack spacing={1.25}>
            {(d?.shortages || []).map((s: any, i: number) => (
              <Box key={i} sx={{ bgcolor: C.panel2, border: `1px solid ${C.bad}33`, borderRadius: 1.5, p: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <WarningAmber sx={{ color: C.bad, fontSize: 18 }} />
                    <Box onClick={() => s.productId && navigate(`/entity/material/${s.productId}`)} sx={{ cursor: s.productId ? 'pointer' : 'default' }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, '&:hover': { color: C.accent } }}>{s.product}</Typography>
                      <Typography sx={{ fontSize: 11, color: C.sub, fontFamily: 'monospace' }}>{s.code}</Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={2.5} alignItems="center">
                    <Box sx={{ textAlign: 'right' }}><Typography sx={{ fontSize: 10, color: C.sub }}>AVAILABLE</Typography><Typography sx={{ fontSize: 14, fontFamily: 'monospace' }}>{s.available}</Typography></Box>
                    <Box sx={{ textAlign: 'right' }}><Typography sx={{ fontSize: 10, color: C.sub }}>REQUIRED</Typography><Typography sx={{ fontSize: 14, fontFamily: 'monospace' }}>{s.required}</Typography></Box>
                    <Box sx={{ textAlign: 'right' }}><Typography sx={{ fontSize: 10, color: C.sub }}>SHORTFALL</Typography><Typography sx={{ fontSize: 14, fontFamily: 'monospace', color: C.bad }}>{s.shortfall}</Typography></Box>
                    <Button size="small" endIcon={<ChevronRight />} onClick={() => navigate('/procurement/requisitions/new')} sx={{ color: C.accent, textTransform: 'none' }}>Create PR</Button>
                  </Stack>
                </Stack>
              </Box>
            ))}
            {!loading && !(d?.shortages || []).length && <Typography sx={{ color: C.good, fontSize: 13, py: 2, textAlign: 'center' }}>No material shortages — stock is healthy.</Typography>}
          </Stack>
        </Box>

        {/* Supplier performance */}
        <Box sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 2, p: 2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1.5 }}>Top Suppliers</Typography>
          <Stack spacing={1}>
            {(d?.topSuppliers || []).map((s: any) => (
              <Box key={s.id} component={RouterLink} to={`/entity/supplier/${s.id}`} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1.25, textDecoration: 'none', color: 'inherit', '&:hover': { color: C.accent } }}>
                <LocalShipping sx={{ color: C.sub, fontSize: 18 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13 }} noWrap>{s.name}</Typography>
                  <Typography sx={{ fontSize: 11, color: C.sub }}>{s.bills} bills</Typography>
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.good }}>{formatIndianNumber(s.spend)}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default MaterialRequirementsControl;
